// src/screens/LeagueCommandCenterScreen.js
// League Command Center — the commissioner's control room. The posting
// (CreateLeagueScreen) recruits the field; THIS is where the competition is
// tuned: round style, format, the calendar (exact day + time of every round
// — founder 2026-08-18), races vs meetings, host mode, track selection
// (searchable dropdown, same UX as event creation), vehicle category, payout
// mode, roster cap, plus the posting's name/description. Everything is
// editable while registration is open; lock freezes it all.
// Server: functions/modules/leagues.js updateLeagueSettings.
import React, { useEffect, useMemo, useState } from 'react';
import {
  View, Text, SafeAreaView, ScrollView, StyleSheet,
  TouchableOpacity, TextInput, ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../../firebaseConfig';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useError } from '../components/ErrorContext';
import GT7RaceConfigPanel from '../components/GT7RaceConfigPanel';
import LeagueCalendarEditor from '../components/LeagueCalendarEditor';
import { GAME_PROFILES, BR_PLATFORM_MAX_PLAYERS } from '../constants/gameData';
import useCritsEnabled from '../hooks/useCritsEnabled';
import { fmtCrits } from '../utils/currencyHelper';
import { RACES_MIN, RACES_MAX, DEFAULT_PATTERN, LEAGUE_ADMIN_MIN_LEAD_MS, resizeRoundTimes, inferPattern } from '../utils/leagueSchedule';

const GOLD = '#FCD000';

// Mirrors functions/constants/leagueEconomy.js + eventEconomy.js
const MIN_PLAYERS = 4;
const MAX_PLAYERS_SOLO = 32;   // solo H2H round-robin
const MAX_TEAMS = 16;          // team leagues (roster entries are TEAMS)
const MAX_PLAYERS_RACE = 16;   // bound by the game lobby
const MAX_PLAYERS_DEDICATED = 15;
const SOLO_SIZES = [4, 5, 6, 7, 8, 10, 12, 14, 16, 20, 24, 28, 32];
// Warzone BR race leagues: ladder of common roster sizes. The pill list is
// windowed per format below to fmt.minPlayers .. effective cap (solo lobby
// cap clamped to BR_PLATFORM_MAX_PLAYERS) - mirrors CreateLeagueScreen.
const BR_ROSTER_SIZES = [4, 5, 6, 7, 8, 10, 12, 14, 16, 20, 24, 28, 32, 36, 40, 44, 48, 50];
const MEETINGS_OPTIONS = [1, 2, 3, 4];
const MAX_DESCRIPTION = 500;
const DAY_ANCHOR_MIN = 1440;

const baseRoundsFor = (n) => (n % 2 === 0 ? n - 1 : n);
const tsMs = (v) => (v?.toMillis ? v.toMillis() : (v?.seconds != null ? v.seconds * 1000 : NaN));

// Pre-calendar leagues (no roundTimesMs) hydrate a calendar from their
// cadence grid so the commissioner sees — and can edit — every date. Saving
// converts the league to an explicit calendar.
const calendarFromCadence = (startMs, totalRounds, gapMin, durationMin) => {
  if (!Number.isFinite(startMs)) return [];
  return Array.from({ length: Math.max(1, totalRounds) }, (_, i) => {
    const offsetMin = gapMin >= DAY_ANCHOR_MIN ? i * gapMin : i * (durationMin + gapMin);
    return startMs + offsetMin * 60000;
  });
};

function Pill({ label, active, onPress, disabled }) {
  return (
    <TouchableOpacity onPress={onPress} disabled={disabled}
      style={[st.pill, active && st.pillActive, disabled && st.pillDisabled]}>
      <Text style={[st.pillText, active && st.pillTextActive]}>{label}</Text>
    </TouchableOpacity>
  );
}

export default function LeagueCommandCenterScreen({ navigation, route }) {
  const leagueId = route?.params?.leagueId;
  const justCreated = route?.params?.justCreated;
  const { showError } = useError();
  const uid = auth.currentUser?.uid || null;
  // Evergreen step 2.3: Crit pool readout hides when the economy is off.
  const critsOn = useCritsEnabled();

  const [league, setLeague] = useState(undefined);
  const [userData, setUserData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedNote, setSavedNote] = useState(false);

  // Dials (hydrated from the league doc once)
  const [hydrated, setHydrated] = useState(false);
  const [roundFormat, setRoundFormat] = useState('h2h');
  const [formatName, setFormatName] = useState(null);
  const [teamSize, setTeamSize] = useState(1);
  const [meetings, setMeetings] = useState(1);
  const [racesCount, setRacesCount] = useState(8);
  const [hostMode, setHostMode] = useState('player');
  const [track, setTrack] = useState(null);           // legacy single track (null = rotate)
  const [raceTracks, setRaceTracks] = useState(null); // per-race calendar (null = rotate all)
  const [gt7Config, setGt7Config] = useState(null);   // laps/pits/tires/endurance + lobby overrides
  const [carCategory, setCarCategory] = useState(null);
  const [payoutMode, setPayoutMode] = useState('podium');
  const [maxPlayers, setMaxPlayers] = useState(8);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // The calendar (founder 2026-08-18): exact start of every round.
  const [roundTimes, setRoundTimes] = useState([]);
  const [pattern, setPattern] = useState(DEFAULT_PATTERN);
  const [scheduleOk, setScheduleOk] = useState(true);
  const [racesInput, setRacesInput] = useState('8');

  // Track dropdown (searchable — same UX as event creation)

  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(doc(db, 'users', uid), (snap) => {
      setUserData(snap.exists() ? snap.data() : null);
    });
    return () => unsub();
  }, [uid]);

  useEffect(() => {
    if (!leagueId) return;
    const unsub = onSnapshot(doc(db, 'leagues', leagueId), (snap) => {
      setLeague(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    }, () => setLeague(null));
    return () => unsub();
  }, [leagueId]);

  useEffect(() => {
    if (!league || hydrated) return;
    // Racing games are always the full-grid series — round-robin is hidden
    // for them (founder 2026-07-14); the server coerces to match.
    const prof = GAME_PROFILES[league.game];
    setRoundFormat(prof?.type === 'racing' ? 'race' : (league.roundFormat || 'h2h'));
    setFormatName(league.formatName || null);
    setTeamSize(Math.max(1, Number(league.teamSize || 1)));
    setMeetings(league.meetingsPerOpponent || 1);
    setRacesCount(league.racesCount || 8);
    setRacesInput(String(league.racesCount || 8));
    setHostMode(league.hostMode || 'player');
    setTrack(league.track || null);
    setRaceTracks(Array.isArray(league.raceTracks) ? league.raceTracks : null);
    setGt7Config(league.gt7Config || null);
    setCarCategory(league.carCategory || null);
    setPayoutMode(league.payoutMode || 'podium');
    setMaxPlayers(league.maxPlayers || 8);
    setName(league.name || '');
    setDescription(league.description || '');
    // Calendar: the stored one, else projected from the cadence grid.
    const storedTimes = Array.isArray(league.roundTimesMs) ? league.roundTimesMs.filter(Number.isFinite) : [];
    let times = storedTimes;
    if (!times.length) {
      const fmtDur = prof?.formats?.[league.formatName]?.durationMinutes || 30;
      times = calendarFromCadence(tsMs(league.startAt), league.totalRounds || 1,
        league.interRoundGapMinutes || 10080, fmtDur);
    }
    // Locked/active/complete: show the REAL calendar — the finalized round
    // count (an h2h league that locked short leaves the tail unused) and any
    // seeded pushes (a late round moves the next one).
    if (league.status !== 'registration' && Number(league.totalRounds) > 0) {
      const total = Math.floor(Number(league.totalRounds));
      times = Array.from({ length: total }, (_, i) => {
        const seeded = Number(league.rounds?.[i + 1]?.startAtMs);
        return Number.isFinite(seeded) && seeded > 0 ? seeded : times[i];
      }).filter(Number.isFinite);
    }
    setRoundTimes(times);
    setPattern(inferPattern(times));
    setHydrated(true);
  }, [league, hydrated]);

  const gp = league ? GAME_PROFILES[league.game] : null;
  const isRacingGame = gp?.type === 'racing';
  // Warzone BR playlists run as race-style full-lobby rounds (placement
  // points) even though COD isn't a racing game. Solos only in v1 - the
  // format's teamSizes are IN-GAME squad sizes, NOT league team sizes.
  const isBRFormat = !!gp?.formats?.[formatName]?.battleRoyale;
  const isRaceLeague = isBRFormat || (isRacingGame && roundFormat === 'race');
  // League BR is solos-only in v1, so BR playlists whose squad sizes don't
  // include Solos (Ranked Play trios) can never validate as a league format —
  // don't offer them as pills.
  const formatNames = useMemo(() => Object.keys(gp?.formats || {}).filter((n) => {
    const f = gp?.formats?.[n];
    if (f?.battleRoyale !== true) return true;
    return (f.teamSizes || []).includes(1);
  }), [gp]);
  const fmtSizes = gp?.formats?.[formatName]?.teamSizes || [1];
  const isTeamLeague = !isBRFormat && teamSize > 1;
  const rosterCount = (league?.rosterUids || []).length;
  // BR: the CODCaster host spectates and never consumes a player slot, so the
  // dedicated-host cap clamp does NOT apply - roster runs to the playlist's
  // SOLO lobby cap (maxPlayersBySquadSize[1] when tuned, else maxPlayers),
  // clamped to BR_PLATFORM_MAX_PLAYERS while scoring is manual, in BOTH host
  // modes. BR rosters are also FLOORED at fmt.minPlayers - below the game's
  // start floor a round could never launch (server: league-roster-below-floor).
  const brFmt = isBRFormat ? gp?.formats?.[formatName] : null;
  const brFloor = Math.max(MIN_PLAYERS, Math.floor(Number(brFmt?.minPlayers)) || MIN_PLAYERS);
  const brRosterCap = Math.min(
    Number(brFmt?.maxPlayersBySquadSize?.[1]) || Number(brFmt?.maxPlayers) || 50,
    BR_PLATFORM_MAX_PLAYERS
  );
  const rosterCap = isBRFormat ? brRosterCap
    : isTeamLeague ? MAX_TEAMS
      : isRaceLeague ? (hostMode === 'dedicated' ? MAX_PLAYERS_DEDICATED : MAX_PLAYERS_RACE)
        : MAX_PLAYERS_SOLO;
  const rosterSizes = isBRFormat
    ? (brFloor >= rosterCap
      ? [rosterCap]
      : [brFloor, ...BR_ROSTER_SIZES.filter((n) => n > brFloor && n < rosterCap), rosterCap])
    : (isTeamLeague || isRaceLeague)
      ? Array.from({ length: rosterCap - MIN_PLAYERS + 1 }, (_, i) => MIN_PLAYERS + i)
      : SOLO_SIZES;
  const isCommissioner = !!uid && (league?.createdByUid === uid || userData?.role === 'admin');
  const editable = league?.status === 'registration' && isCommissioner;
  const locked = !!league && league.status !== 'registration';
  const totalRounds = locked && Number(league.totalRounds) > 0
    ? Math.floor(Number(league.totalRounds))
    : (isRaceLeague ? racesCount : meetings * baseRoundsFor(maxPlayers));

  // Season end for the schedule box.
  const [seasonEnd, setSeasonEnd] = useState(null);
  useEffect(() => {
    httpsCallable(getFunctions(undefined, 'us-central1'), 'getSeason')({})
      .then(({ data }) => setSeasonEnd(data?.endsAt || null))
      .catch(() => setSeasonEnd(null));
  }, []);

  const mark = (setter) => (v) => { setter(v); setDirty(true); setSavedNote(false); };

  const roundNoun = isRaceLeague ? (isBRFormat ? 'match' : 'race') : 'round';
  const durationMin = gp?.formats?.[formatName]?.durationMinutes || 30;
  const setRaces = (n) => {
    const v = Math.min(RACES_MAX, Math.max(RACES_MIN, Math.floor(Number(n) || RACES_MIN)));
    mark(setRacesCount)(v);
    setRacesInput(String(v));
  };
  // Keep one calendar row per round as the dials change (races, meetings,
  // roster) — extend along the pattern, truncate the tail.
  useEffect(() => {
    if (!hydrated) return;
    setRoundTimes((prev) => (prev.length === totalRounds ? prev
      : resizeRoundTimes(prev, totalRounds, pattern, tsMs(league?.startAt) || Date.now() + 86400000)));
  }, [totalRounds, hydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSave = async () => {
    setSaving(true);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'updateLeagueSettings');
      const { data } = await fn({
        leagueId,
        // BR formats are race leagues (server coerces too); solos only in v1.
        roundFormat: isBRFormat ? 'race' : roundFormat,
        formatName,
        teamSize: isBRFormat ? 1 : teamSize,
        meetingsPerOpponent: isRaceLeague ? undefined : meetings,
        racesCount: isRaceLeague ? racesCount : undefined,
        hostMode: (isRaceLeague || isTeamLeague) ? hostMode : undefined,
        // Track/car/lobby dials are racing-game only - a BR race league has
        // no GT7 config to send.
        track: (isRaceLeague && isRacingGame) ? (track || 'rotate') : undefined,
        raceTracks: (isRaceLeague && isRacingGame) ? raceTracks : undefined,
        gt7Config: (isRaceLeague && isRacingGame) ? gt7Config : undefined,
        carCategory: (isRaceLeague && isRacingGame) ? carCategory : undefined,
        // The calendar: every round's exact start (round 1 = league start).
        roundTimesMs: roundTimes,
        payoutMode,
        maxPlayers,
        name: name.trim(),
        description,
      });
      setDirty(false);
      setSavedNote(true);
      console.log(`[CommandCenter] saved — ${data.totalRounds} rounds`);
    } catch (err) {
      const msg = String(err?.message || '');
      if (msg.includes('league-name-taken:')) {
        showError('Name already in use', msg.split('league-name-taken:')[1] || 'A league with that name already exists.');
      } else if (msg.includes('title-not-approved:')) {
        showError('League name not approved', msg.split('title-not-approved:')[1] || 'Pick a different name.');
      } else if (msg.includes('description-not-approved:')) {
        showError('Description not approved', msg.split('description-not-approved:')[1] || 'Reword your description.');
      } else if (msg.includes('league-schedule-')) {
        showError('Check the schedule', msg.split(/league-schedule-[a-z]+:/)[1]?.trim() || 'One of the dates needs a look.');
      } else if (msg.includes('league-start-too-soon:')) {
        showError('Starts too soon', msg.split('league-start-too-soon:')[1]?.trim() || 'Move round 1 later.');
      } else {
        showError('Could not save settings', msg.replace(/^[a-z-]+:\s*/i, '') || 'Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const onCancelLeague = async () => {
    if (typeof window !== 'undefined' && !window.confirm(critsOn ? 'Cancel this league? Everyone (including you) is refunded in full.' : 'Cancel this league? Everyone is removed from it.')) return;
    setSaving(true);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'cancelLeague');
      await fn({ leagueId });
      navigation.navigate('Lobby');
    } catch (err) {
      showError('Could not cancel', String(err?.message || 'Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  if (league === undefined || (league && !hydrated)) {
    return (
      <SafeAreaView style={st.safe}>
        <Header navigation={navigation} />
        <ActivityIndicator color={GOLD} style={{ marginTop: 60 }} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }
  // The Command Center is VIEWABLE by everyone (founder 2026-07-14) —
  // captains and prospective joiners can see exactly how the competition is
  // configured. Only the commissioner can change anything (server-enforced).
  if (league === null) {
    return (
      <SafeAreaView style={st.safe}>
        <Header navigation={navigation} />
        <Text style={[st.note, { margin: 24 }]}>This league doesn't exist.</Text>
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={st.safe}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={st.container}>
        <View style={st.titleRow}>
          <Text style={st.title}>⚙️ Command Center</Text>
          <TouchableOpacity onPress={() => navigation.navigate('LeagueDetail', { leagueId })}>
            <Text style={st.viewPostingLink}>View posting →</Text>
          </TouchableOpacity>
        </View>
        <Text style={st.subtitle}>{league.name}</Text>
        {justCreated && (
          <View style={st.welcomeBox}>
            <Text style={st.welcomeText}>
              League posted! These are your commissioner controls — dial in the competition
              before it locks (15 minutes before start). Defaults work fine if you change nothing.
            </Text>
          </View>
        )}
        {!editable && (
          <View style={st.frozenBox}>
            <Text style={st.frozenText}>
              {league.status !== 'registration'
                ? 'The league is locked — settings are frozen and the schedule is drawn. Standings live on the league page.'
                : 'Viewing the league\'s configuration — only the commissioner can change these settings.'}
            </Text>
          </View>
        )}

        {/* Racing leagues are always the full-grid championship series —
            no round-style choice, and the lap-preset formats are superseded
            by Race Configuration's laps modes (founder 2026-07-14). */}
        {isRaceLeague && (
          <Text style={[st.note, { marginTop: 10 }]}>
            {isBRFormat
              ? 'Battle royale series - every round is one full-lobby private match with the whole roster, scored on placement points (P1 = 30, down through P20).'
              : 'Championship series — every round is one race with the whole roster, scored on a points system (25-18-15-12-10-8-6-4-2-1).'}
          </Text>
        )}

        {/* Format (non-racing games — e.g. COD modes) */}
        {!isRacingGame && formatNames.length > 1 && (
          <>
            <Text style={st.label}>Format</Text>
            <View style={st.pillRow}>
              {formatNames.map((n) => (
                <Pill key={n} label={n} active={formatName === n}
                  onPress={() => {
                    mark(setFormatName)(n);
                    const nextIsBR = !!gp?.formats?.[n]?.battleRoyale;
                    if (nextIsBR) {
                      // BR playlists run race-style and are SOLOS only in v1
                      // (the format's teamSizes are in-game squad sizes).
                      setRoundFormat('race');
                      setTeamSize(1);
                      // Snap the roster dial into the BR floor..cap window -
                      // below fmt.minPlayers a round could never launch and
                      // the save would bounce off league-roster-below-floor.
                      const nf = gp?.formats?.[n] || {};
                      const nfFloor = Math.max(MIN_PLAYERS, Math.floor(Number(nf.minPlayers)) || MIN_PLAYERS);
                      const nfCap = Math.min(
                        Number(nf.maxPlayersBySquadSize?.[1]) || Number(nf.maxPlayers) || 50,
                        BR_PLATFORM_MAX_PLAYERS
                      );
                      setMaxPlayers((m) => Math.min(Math.max(Number(m) || nfFloor, nfFloor), nfCap));
                      return;
                    }
                    if (roundFormat === 'race' && !isRacingGame) setRoundFormat('h2h');
                    // Keep teamSize sanctioned for the new format.
                    const sizes = gp?.formats?.[n]?.teamSizes || [1];
                    const nextTeam = sizes.includes(teamSize) ? teamSize
                      : (sizes.includes(1) ? 1 : (gp?.formats?.[n]?.defaultTeamSize || sizes[0]));
                    if (nextTeam !== teamSize) setTeamSize(nextTeam);
                    // Clamp the roster dial: switching BR (cap 50) back to a
                    // CDL format drops the cap to 16/32 — a stale 50 would
                    // just bounce off updateLeagueSettings validation.
                    const nextCap = nextTeam > 1 ? MAX_TEAMS : MAX_PLAYERS_SOLO;
                    setMaxPlayers((m) => Math.min(Number(m) || nextCap, nextCap));
                  }}
                  disabled={!editable} />
              ))}
            </View>
            {isBRFormat && (
              <Text style={st.note}>
                Warzone leagues are Solos in v1 - squads coming later.
              </Text>
            )}
          </>
        )}

        {/* Team size (formats with multiple sanctioned sizes, e.g. COD 6/5/4/3/2).
            Hidden for BR playlists - their teamSizes are in-game SQUAD sizes,
            and league BR is solos-only in v1. */}
        {!isBRFormat && fmtSizes.length > 1 && (
          <>
            <Text style={st.label}>Team size</Text>
            <View style={st.pillRow}>
              {[...fmtSizes].sort((a, b) => b - a).map((n) => (
                <Pill key={n} label={n === 1 ? 'Solo' : `${n}v${n}`} active={teamSize === n}
                  onPress={mark(setTeamSize).bind(null, n)}
                  disabled={!editable || rosterCount > 0} />
              ))}
            </View>
            {rosterCount > 0 && (
              <Text style={st.note}>Team size locks once anyone joins — rosters were validated against it.</Text>
            )}
          </>
        )}

        {/* Lobby host (team leagues) — leagues are the only place events run
            without a dedicated host, creator's choice. */}
        {isTeamLeague && (
          <>
            <Text style={st.label}>Lobby host</Text>
            <View style={st.pillRow}>
              <Pill label="Captains host (home rotates)" active={hostMode === 'player'} onPress={mark(setHostMode).bind(null, 'player')} disabled={!editable} />
              <Pill label="Host program" active={hostMode === 'dedicated'} onPress={mark(setHostMode).bind(null, 'dedicated')} disabled={!editable} />
            </View>
            <Text style={st.note}>
              {hostMode === 'player'
                ? 'Each round, the HOME team\'s captain creates the lobby and sends the invites — home/away flips every meeting, so hosting duty rotates. Captains can coordinate internally.'
                : 'A host-program host (CODCaster for COD) can claim and run each match\'s lobby.'}
            </Text>
          </>
        )}

        {/* h2h: meetings */}
        {!isRaceLeague && (
          <>
            <Text style={st.label}>Play each opponent</Text>
            <View style={st.pillRow}>
              {MEETINGS_OPTIONS.map((k) => (
                <Pill key={k} label={`${k}×`} active={meetings === k} onPress={mark(setMeetings).bind(null, k)} disabled={!editable} />
              ))}
            </View>
            <Text style={st.note}>
              {meetings === 2 ? 'Home-and-away, like division rivals. ' : ''}
              {totalRounds} rounds at a full {maxPlayers}-player roster.
            </Text>
          </>
        )}

        {/* race: races / host mode / track / car class. BR playlists reuse
            this section with "match" wording (each round = one BR match). */}
        {isRaceLeague && (
          <>
            <Text style={st.label}>{isBRFormat ? 'Number of matches' : 'Number of races'}</Text>
            <View style={st.stepperRow}>
              <TouchableOpacity style={st.stepBtn} onPress={() => setRaces(racesCount - 1)} disabled={!editable || racesCount <= RACES_MIN}>
                <Ionicons name="remove" size={18} color={!editable || racesCount <= RACES_MIN ? '#555' : '#fff'} />
              </TouchableOpacity>
              <TextInput
                style={st.stepInput}
                value={racesInput}
                onChangeText={setRacesInput}
                onBlur={() => setRaces(racesInput)}
                onSubmitEditing={() => setRaces(racesInput)}
                keyboardType="numeric"
                maxLength={3}
                editable={editable}
              />
              <TouchableOpacity style={st.stepBtn} onPress={() => setRaces(racesCount + 1)} disabled={!editable || racesCount >= RACES_MAX}>
                <Ionicons name="add" size={18} color={!editable || racesCount >= RACES_MAX ? '#555' : '#fff'} />
              </TouchableOpacity>
              <View style={[st.pillRow, { marginLeft: 10, flex: 1 }]}>
                {[4, 6, 8, 10, 12, 16, 20].map((n) => (
                  <Pill key={n} label={String(n)} active={racesCount === n} onPress={() => setRaces(n)} disabled={!editable} />
                ))}
              </View>
            </View>
            <Text style={st.note}>Any number from {RACES_MIN} to {RACES_MAX} — the calendar below gets one row per {roundNoun}.</Text>

            <Text style={st.label}>Lobby host</Text>
            <View style={st.pillRow}>
              <Pill label={isBRFormat ? 'Players host (rotates)' : 'Full grid (players host)'} active={hostMode === 'player'} onPress={mark(setHostMode).bind(null, 'player')} disabled={!editable} />
              {/* BR: the CODCaster spectates - no player slot consumed, so no
                  roster clamp on the dedicated option. */}
              <Pill label="Dedicated host" active={hostMode === 'dedicated'}
                onPress={mark(setHostMode).bind(null, 'dedicated')}
                disabled={!editable || (!isBRFormat && rosterCount > MAX_PLAYERS_DEDICATED)} />
            </View>
            <Text style={st.note}>
              {isBRFormat
                ? (hostMode === 'player'
                  ? (critsOn
                    ? 'Hosting duty rotates through the roster; the round host creates the private match and shares the game code. Player-hosts earn no host reward.'
                    : 'Hosting duty rotates through the roster; the round host creates the private match and shares the game code.')
                  : 'A CODCaster host spectates and runs each private match - they never take a player slot. Unclaimed round = voided.')
                : (hostMode === 'player'
                  ? (critsOn
                    ? `All ${MAX_PLAYERS_RACE} lobby slots race; hosting duty rotates through the roster. Player-hosts earn no host reward.`
                    : `All ${MAX_PLAYERS_RACE} lobby slots race; hosting duty rotates through the roster.`)
                  : `A neutral host runs each lobby (takes a slot — ${MAX_PLAYERS_DEDICATED} racers max). Unclaimed round = voided.`)}
            </Text>

            {/* GT7 race configuration — SAME sections as event creation:
                Race Configuration, Vehicle Category, Track Selection per
                race, Advanced Settings (founder 2026-07-14). Racing games
                only - BR leagues have no laps/tracks/car dials. */}
            {isRacingGame && (
              <GT7RaceConfigPanel
                gp={gp}
                editable={editable}
                racesCount={racesCount}
                carCategory={carCategory}
                onCarCategory={mark(setCarCategory)}
                raceTracks={raceTracks}
                onRaceTracks={mark(setRaceTracks)}
                gt7Config={gt7Config}
                onGt7Config={mark(setGt7Config)}
              />
            )}
          </>
        )}

        {/* Calendar — the exact day + time of every round (founder 2026-08-18).
            Editable until lock; everyone else sees the dates read-only. */}
        <Text style={st.label}>Calendar</Text>
        {editable && (
          <Text style={st.note}>
            Every {roundNoun} has its own date and time. Set a rhythm to fill them, then adjust any row.
            {(league?.rosterUids || []).some((u) => u !== uid) ? ' Members are notified when the calendar changes.' : ''}
          </Text>
        )}
        <LeagueCalendarEditor
          roundTimes={roundTimes}
          onChange={mark(setRoundTimes)}
          pattern={pattern}
          onPattern={setPattern}
          totalRounds={totalRounds}
          roundNoun={roundNoun}
          durationMinutes={durationMin}
          editable={editable}
          originalFirstMs={tsMs(league?.startAt) || null}
          seasonEnd={league?.status === 'registration' ? seasonEnd : null}
          gamesPerEntrant={isRaceLeague ? null : meetings * (maxPlayers - 1)}
          entrantNoun={isTeamLeague ? 'team' : 'entrant'}
          minLeadMs={userData?.role === 'admin' ? LEAGUE_ADMIN_MIN_LEAD_MS : undefined}
          onValidity={setScheduleOk}
        />

        {/* Payout + roster. Payout mode is a Crits-economy dial: hidden with
            Crits off (placements are never paid). The hydrated payoutMode is
            still sent on save, unchanged. */}
        {critsOn && (
          <>
            <Text style={st.label}>Payout mode</Text>
            <View style={st.pillRow}>
              <Pill label="Winner takes all" active={payoutMode === 'winner'} onPress={mark(setPayoutMode).bind(null, 'winner')} disabled={!editable} />
              <Pill label="Podium" active={payoutMode === 'podium'} onPress={mark(setPayoutMode).bind(null, 'podium')} disabled={!editable} />
              <Pill label="Tiered" active={payoutMode === 'tiered'} onPress={mark(setPayoutMode).bind(null, 'tiered')} disabled={!editable} />
            </View>
          </>
        )}

        <Text style={st.label}>Roster cap ({isTeamLeague ? 'teams' : 'players'})</Text>
        <View style={st.pillRow}>
          {rosterSizes.map((n) => (
            <Pill key={n} label={String(n)} active={maxPlayers === n}
              onPress={mark(setMaxPlayers).bind(null, n)}
              disabled={!editable || n < rosterCount} />
          ))}
        </View>
        {isBRFormat && (
          <Text style={st.note}>
            Warzone leagues need at least {brFloor} players in the lobby to launch each match.
          </Text>
        )}
        {rosterCount > 0 && (
          <Text style={st.note}>{rosterCount} joined — the cap can't drop below them.{critsOn ? ` Crit pool so far: ${fmtCrits(league.potCount || 0)}.` : ''}</Text>
        )}

        {/* Posting text */}
        <Text style={st.label}>League name</Text>
        <TextInput style={st.input} value={name} onChangeText={mark(setName)} maxLength={80} editable={editable}
          placeholderTextColor="#666" />
        <Text style={st.label}>Description</Text>
        <TextInput style={[st.input, st.multiline]} value={description} onChangeText={mark(setDescription)}
          maxLength={MAX_DESCRIPTION} multiline numberOfLines={4} editable={editable}
          placeholder="Pitch your league..." placeholderTextColor="#666" />
        <Text style={st.note}>Name and description changes are re-reviewed on save.</Text>

        {/* Save / cancel */}
        {editable && (
          <>
            <TouchableOpacity style={[st.saveBtn, (!dirty || saving || !scheduleOk) && st.saveBtnDisabled]}
              disabled={!dirty || saving || !scheduleOk} onPress={onSave}>
              {saving ? <ActivityIndicator color="#000" /> : <Text style={st.saveBtnText}>
                {savedNote && !dirty ? 'Saved ✓' : (!scheduleOk ? 'Fix the calendar to save' : 'Save settings')}
              </Text>}
            </TouchableOpacity>
            {savedNote && !dirty && (
              <Text style={[st.note, { textAlign: 'center' }]}>Settings saved — they freeze when the league locks.</Text>
            )}
            <TouchableOpacity style={st.dangerBtn} disabled={saving} onPress={onCancelLeague}>
              <Text style={st.dangerBtnText}>{critsOn ? 'Cancel league (refund everyone)' : 'Cancel league'}</Text>
            </TouchableOpacity>
          </>
        )}
      </ScrollView>
      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

const st = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#0b0b0b' },
  container: { padding: 14, paddingBottom: 120, maxWidth: 760, width: '100%', alignSelf: 'center' },
  titleRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  title: { color: '#fff', fontSize: 22, fontWeight: '900' },
  viewPostingLink: { color: '#4FC3F7', fontSize: 13, fontWeight: '800' },
  subtitle: { color: GOLD, fontSize: 14, fontWeight: '800', marginTop: 2, marginBottom: 10 },
  welcomeBox: {
    backgroundColor: '#101010', borderLeftWidth: 3, borderLeftColor: '#2ECC71',
    borderRadius: 8, padding: 10, marginBottom: 10,
  },
  welcomeText: { color: '#cfcfcf', fontSize: 12, lineHeight: 17 },
  frozenBox: {
    backgroundColor: '#101010', borderLeftWidth: 3, borderLeftColor: '#e74c3c',
    borderRadius: 8, padding: 10, marginBottom: 10,
  },
  frozenText: { color: '#cfcfcf', fontSize: 12, lineHeight: 17 },
  label: { color: '#ddd', fontSize: 13, fontWeight: '800', marginTop: 14, marginBottom: 6 },
  note: { color: '#8a8a8a', fontSize: 11, marginTop: 6, lineHeight: 15 },
  pillRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  pill: {
    borderWidth: 1, borderColor: '#2a2a2a', backgroundColor: '#151515',
    paddingVertical: 7, paddingHorizontal: 12, borderRadius: 10,
  },
  pillActive: { backgroundColor: GOLD, borderColor: GOLD },
  pillDisabled: { opacity: 0.4 },
  pillText: { color: '#ddd', fontWeight: '800', fontSize: 12 },
  pillTextActive: { color: '#000' },
  input: {
    backgroundColor: '#0f1115', borderColor: '#2a2f36', borderWidth: 1,
    borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10,
    color: '#fff', fontSize: 14, marginTop: 2,
  },
  multiline: { minHeight: 96, textAlignVertical: 'top' },
  dropdown: {
    backgroundColor: '#1b1e24', borderRadius: 8, borderWidth: 1,
    borderColor: '#2a2f36', maxHeight: 340, marginTop: 4,
  },
  dropdownRow: { padding: 10, borderBottomWidth: 1, borderBottomColor: '#1e2025' },
  dropdownRowOn: { backgroundColor: '#2a2f36' },
  dropdownCat: {
    color: '#ffd166', fontSize: 12, fontWeight: '700', paddingHorizontal: 10,
    paddingTop: 12, paddingBottom: 4, backgroundColor: '#0d0f12',
    textTransform: 'uppercase', letterSpacing: 0.5,
  },
  dropdownBucket: { color: '#888', fontSize: 10, paddingHorizontal: 10, paddingTop: 6, paddingBottom: 2 },
  stepperRow: { flexDirection: 'row', alignItems: 'center', marginTop: 4, flexWrap: 'wrap' },
  stepBtn: {
    width: 36, height: 36, borderRadius: 8, borderWidth: 1, borderColor: '#2a2f36',
    backgroundColor: '#151515', alignItems: 'center', justifyContent: 'center',
  },
  stepInput: {
    width: 64, height: 36, marginHorizontal: 6, textAlign: 'center',
    backgroundColor: '#0f1115', borderColor: '#2a2f36', borderWidth: 1, borderRadius: 8,
    color: GOLD, fontSize: 15, fontWeight: '900',
  },
  saveBtn: {
    backgroundColor: GOLD, borderRadius: 12, paddingVertical: 14,
    alignItems: 'center', marginTop: 18,
  },
  saveBtnDisabled: { opacity: 0.4 },
  saveBtnText: { color: '#000', fontSize: 15, fontWeight: '900' },
  dangerBtn: {
    borderColor: '#e74c3c', borderWidth: 1, borderRadius: 10, paddingVertical: 10,
    alignItems: 'center', marginTop: 12,
  },
  dangerBtnText: { color: '#e74c3c', fontWeight: '800', fontSize: 13 },
});
